import { produce } from 'immer';
import { ContextType, createContext, ReactNode, useMemo, useState } from 'react';

import useMediaLibrarySettings from '~modules/media-library/context/useMediaLibrarySettings';
import useMediaLibraryUserPreferences from '~modules/media-library/context/useMediaLibraryUserPreferences';
import { splitFilterOptionId } from '~modules/media-library/model/FilterHelper';
import { DEFAULT_SORT_KEY } from '~modules/media-library/model/SortOption';
import { ViewContext } from '~modules/media-library/model/ViewContext';
import { ViewVariant } from '~modules/media-library/model/ViewVariant';

type ContextStateShape = {
  searchTerm: string;
};

export const MediaLibrarySearchQueryContext = createContext<
  | {
      // values
      searchTerm: string;
      searchQuery: string;
      // handlers
      onSetSearchTerm: (_text?: string) => void;
    }
  | undefined
>(undefined);

const DEFAULT_KEY = 'DEFAULT';

/**
 * Helper function that concats the filterQuery with the searchTerm
 */
function generateSearchQuery(filterQuery = '', searchTerm = '') {
  const params = new URLSearchParams(filterQuery);
  const sanitizedInputQuery = searchTerm.trim();
  if (!!sanitizedInputQuery) {
    params.append('searchString', sanitizedInputQuery);
  }
  // decode url, to prevent dopple encoding, by api get method
  return decodeURIComponent(params.toString());
}

export function MediaLibrarySearchQueryProvider({ children }: { children: ReactNode }) {
  // read settings
  const { idCollection, viewContext } = useMediaLibrarySettings();

  // read user preferences
  const { selectedFilterValueIds, selectedTabFilterId, showFavouritesOnly, sortKey, sortOrder, viewVariant } =
    useMediaLibraryUserPreferences();

  // prepare state for context
  const [contextState, setContextState] = useState<Record<string, ContextStateShape>>({
    [DEFAULT_KEY]: {
      searchTerm: '',
    },
  });

  // Handler
  const handler = useMemo(
    () => ({
      onSetSearchTerm: (text: string = '') =>
        setContextState(state =>
          produce(state, draft => {
            draft[idCollection || DEFAULT_KEY] = { searchTerm: text };
          }),
        ),
    }),
    [idCollection],
  );

  const filterQuery = useMemo(() => {
    const params = new URLSearchParams();
    for (const filterValue of selectedFilterValueIds || []) {
      const [key, value] = splitFilterOptionId(filterValue);
      if (!params.has(key)) {
        params.append(key, value);
      } else {
        params.set(key, `${params.get(key)},${value}`);
      }
    }

    if (showFavouritesOnly || (viewContext === ViewContext.FAVOURITE && !Boolean(idCollection))) {
      params.append('Favourites', 'true');
    }

    // Optional TabFilter
    if (selectedTabFilterId && selectedTabFilterId !== '') {
      const [key, value] = splitFilterOptionId(selectedTabFilterId);
      if (!params.has(key)) {
        params.append(key, value);
      } else {
        params.set(key, `${params.get(key)},${value}`);
      }
    }

    if (!!sortKey && sortKey !== DEFAULT_SORT_KEY) {
      // Omit sort parameter when Default is selected
      params.append('sort', sortKey);
    }
    if (!!sortOrder && sortKey !== DEFAULT_SORT_KEY) {
      // Omit order parameter when Default  as sortKey is selected
      params.append('order', sortOrder);
    }
    if (!!viewVariant) {
      params.append('result_view', viewVariant);

      // load more items for list and small view
      if (viewVariant === ViewVariant.LIST || viewVariant === ViewVariant.TILE_SMALL) params.append('rows', '30');
    }

    return params.toString();
  }, [
    idCollection,
    selectedFilterValueIds,
    selectedTabFilterId,
    showFavouritesOnly,
    sortKey,
    sortOrder,
    viewContext,
    viewVariant,
  ]);

  const value: ContextType<typeof MediaLibrarySearchQueryContext> = useMemo(() => {
    const searchTerm = contextState[idCollection || DEFAULT_KEY]?.searchTerm || '';
    return {
      searchTerm,
      ...handler,
      searchQuery: generateSearchQuery(filterQuery, searchTerm),
    };
  }, [contextState, idCollection, handler, filterQuery]);

  return <MediaLibrarySearchQueryContext.Provider value={value}>{children}</MediaLibrarySearchQueryContext.Provider>;
}
